/* global React, Icon, Card, CardHead, Button, Modal, Field, Input, SearchSelect, Badge, useState, useEffect, t, fmt */
// ============================================================
// PLUMME 2.0 - Kreditnota-felt i Nytt salg
// ============================================================
// Kreditnotaen i «Nytt salg» er en fri kreditnota: den kan staa paa egne bein
// uten aa vaere laget av en faktura i lista. Bokfoeringsforskriften § 5-2-7 krever
// likevel at en kreditnota refererer til det opprinnelige salget, og
// bokfoeringsloven § 10 krever at grunnlaget viser det reelle innholdet. Derfor er
// aarsaken paakrevd, og referansen enten en peker til en faktura i lista eller
// fritekst for salg som ligger utenfor appen (gammelt system, avtale, periode).
//
// Feltene ligger i en modal, ikke i selve kortet. Forklaringene de trenger er
// lange nok til at de dominerte skjemaet, og kreditering er noe man gjor én gang
// per dokument — ikke et felt man hopper inn og ut av. Kortet viser derfor bare
// hva som er satt, og om det som mangler stopper opprettingen.
//
// Fortegn og bokfoering ligger i `src/services/backend.jsx`
// (`invoicePostingFactor`), som snur hele bilaget naar `kind === 'credit'`.
// Brukeren skriver derfor positive belop, som paa en faktura.

// Fakturaer kreditnotaen kan kobles til. Bare bokfoerte fakturaer er relevante —
// et utkast er ikke et salg enda, og et tilbud har aldri vaert det.
function creditNoteSortableDate(row) {
  const value = String(row?.date || '');
  const norwegian = value.match(/^(\d{1,2})\.(\d{1,2})\.(\d{4})$/);
  return norwegian ? `${norwegian[3]}-${norwegian[2].padStart(2, '0')}-${norwegian[1].padStart(2, '0')}` : value;
}

function creditNoteCandidateRows(rows, customer) {
  const customerId = String(customer?.id || '');
  const customerName = String(customer?.name || '').trim().toLowerCase();
  return (rows || [])
    .filter((row) => row.type === 'faktura' && row.status !== 'utkast')
    .filter((row) => row.fullyCredited !== true)
    .filter((row) => {
      if (!customer) return true;
      const rowId = String(row._backend?.customerId || row._backend?.customer?.id || '');
      if (customerId && rowId) return customerId === rowId;
      return String(row.customer || '').trim().toLowerCase() === customerName;
    })
    .sort((left, right) => {
      const outstanding = Number(right.remainingAmount > 0.005) - Number(left.remainingAmount > 0.005);
      if (outstanding) return outstanding;
      return creditNoteSortableDate(right).localeCompare(creditNoteSortableDate(left))
        || String(right.id || '').localeCompare(String(left.id || ''));
    });
}

function creditNoteLinkOptions(customer) {
  const rows = window.PlummeBackend?.getInvoiceListModel?.({})?.rows || [];
  return creditNoteCandidateRows(rows, customer);
}

const creditNoteReferenceValue = ({ options, invoiceId, reference }) => {
  const linked = (options || []).find((row) => row.id === invoiceId);
  return String(linked?.num || reference || '').trim();
};

// Direct invoice crediting creates line-level source references. They make the
// originating invoice authoritative, while a manually created credit note keeps
// the link editable until the user chooses one.
const creditNoteSourceIsLocked = (editor) => Boolean(
  editor?.creditedFromInvoiceId
  && (editor?.lines || []).some((line) => String(line?.creditedFromLineId || '').trim()),
);

function creditNoteSearchOptions(rows) {
  return [
    { value: '', label: t('sale.credit.linkNone'), featured: true },
    ...(rows || []).map((row, index) => ({
      value: row.id,
      label: t('sale.credit.invoiceOption', { number: row.num || t('sale.credit.noNumber'), customer: row.customer || '-' }),
      description: t('sale.credit.invoiceOptionMeta', { date: row.date || '-', amount: fmt(row.amount || 0) }),
      searchText: String(row.amount || ''),
      featured: index < 15,
    })),
  ];
}

/**
 * Selve skjemaet. Verdiene redigeres lokalt og skrives til editoren naar brukeren
 * lagrer, slik kundedetaljene i salgsflyten gjoer. Lukker man modalen uten aa
 * lagre, staar det som var der foer.
 */
function CreditNoteDialog({ s, onClose }) {
  const [options, setOptions] = useState(() => creditNoteLinkOptions(s.customer));
  const [reason, setReason] = useState(s.creditReason || '');
  const [reference, setReference] = useState(s.creditReference || '');
  const [invoiceId, setInvoiceId] = useState(s.creditedFromInvoiceId || '');
  const sourceLocked = creditNoteSourceIsLocked(s);
  useEffect(() => {
    const refresh = () => setOptions(creditNoteLinkOptions(s.customer));
    refresh();
    window.addEventListener('plumme-backend-state', refresh);
    return () => window.removeEventListener('plumme-backend-state', refresh);
  }, [s.customer?.id, s.customer?.name]);
  const linked = options.find((row) => row.id === invoiceId)
    || (sourceLocked && invoiceId ? { id: invoiceId, num: reference || s.creditReference || '-' } : null);
  const effectiveReference = creditNoteReferenceValue({ options, invoiceId, reference });
  const canSave = Boolean(reason.trim() && effectiveReference);
  const pickInvoice = (nextId) => {
    setInvoiceId(nextId);
    const picked = options.find((row) => row.id === nextId);
    // A linked invoice owns the document reference. Without a link, the user
    // supplies the external sale reference explicitly.
    setReference(picked?.num ? String(picked.num) : '');
  };
  const save = () => {
    s.setCreditReason(reason.trim());
    s.setCreditReference(effectiveReference);
    s.setCreditedFromInvoiceId(invoiceId);
    onClose();
  };
  return <Modal open onClose={onClose} max={620} title={t('sale.credit.title')}
    footer={<React.Fragment>
      <Button variant="ghost" onClick={onClose}>{t('common.cancel')}</Button>
      <Button icon="check" disabled={!canSave} title={!reason.trim() ? t('sale.credit.needReason') : !effectiveReference ? t('sale.credit.needReference') : undefined} onClick={save}>{t('sale.save')}</Button>
    </React.Fragment>}>
    <div className="col gap-14" style={{ paddingBottom: 4 }}>
      <p className="t-sm muted" style={{ margin: 0, fontWeight: 600 }}>{t('sale.credit.help')}</p>
      <Field label={t('sale.credit.reason')} hint={t('sale.credit.reasonHint')} help={t('helpTopics.credit.body')} helpTitle={t('helpTopics.credit.title')}>
        <Input autoFocus placeholder={t('sale.credit.reasonPh')} value={reason} onChange={(event) => setReason(event.target.value)} />
      </Field>
      {!sourceLocked && <Field label={t('sale.credit.link')} hint={t('sale.credit.linkHint')} help={t('helpTopics.credit.body')} helpTitle={t('helpTopics.credit.title')}>
        <SearchSelect
          portal
          value={invoiceId}
          onChange={pickInvoice}
          options={creditNoteSearchOptions(options)}
          placeholder={t('sale.credit.linkNone')}
          searchPlaceholder={t('sale.credit.linkSearchPh')}
          emptyLabel={t('sale.credit.linkNoMatches')}
          expandLabel={t('sale.credit.linkShowMore')}
          max={340}
        />
      </Field>}
      {!linked && <Field label={t('sale.credit.reference')} hint={t('sale.credit.referenceHint')} help={t('helpTopics.credit.body')} helpTitle={t('helpTopics.credit.title')}>
        <Input placeholder={t('sale.credit.referencePh')} value={reference} onChange={(event) => setReference(event.target.value)} />
      </Field>}
      {linked
        ? <div className="row gap-10" style={{ padding: '11px 14px', background: 'var(--plum-50)', border: '1px solid var(--plum-200)', borderRadius: 'var(--r-ms)' }}>
          <span className="icon-chip" style={{ width: 28, height: 28, borderRadius: 'var(--r-sm)', flex: 'none' }}><Icon name="link" size={15} /></span>
          <span className="t-sm" style={{ fontWeight: 700 }}>{t('sale.credit.linkedTo', { document: linked.num || '-' })}</span>
          <Badge variant="info">{t('sale.credit.offsetBadge')}</Badge>
          {sourceLocked && <span className="t-xs muted" style={{ marginLeft: 'auto', fontWeight: 700 }}>{t('sale.credit.linkLocked')}</span>}
        </div>
        : <div className="t-sm muted" style={{ fontWeight: 600 }}>{t('sale.credit.freeHint')}</div>}
    </div>
  </Modal>;
}

function CreditNoteSummaryRow({ label, value }) {
  return <div className="row gap-8" style={{ alignItems: 'baseline', minWidth: 0 }}>
    <span className="t-xs muted" style={{ fontWeight: 800, textTransform: 'uppercase', letterSpacing: '.04em', flex: 'none' }}>{label}</span>
    <span className="t-sm" style={{ fontWeight: 700, minWidth: 0, overflowWrap: 'anywhere' }}>{value}</span>
  </div>;
}

function CreditNoteSection(s) {
  const [open, setOpen] = useState(false);
  const reason = String(s.creditReason || '').trim();
  const reference = String(s.creditReference || '').trim();
  return <Card>
    <CardHead icon="files" title={t('sale.credit.title')} help={t('sale.credit.help')}
      right={<Button size="sm" variant={reason ? 'secondary' : 'primary'} icon={reason ? 'pencil' : 'plus'} onClick={() => setOpen(true)}>
        {reason ? t('sale.credit.edit') : t('sale.credit.set')}
      </Button>} />
    {reason
      ? <div className="col gap-8">
        <CreditNoteSummaryRow label={t('sale.credit.previewReason')} value={reason} />
        <CreditNoteSummaryRow label={t('sale.credit.previewReference')} value={reference || t('sale.credit.noReference')} />
        {s.creditedFromInvoiceId
          ? <div className="row gap-8"><Badge variant="info">{t('sale.credit.offsetBadge')}</Badge></div>
          : <div className="row gap-8"><Badge variant="muted">{t('sale.credit.freeBadge')}</Badge></div>}
      </div>
      : <div className="alert alert-warning" style={{ margin: 0 }}>
        <span className="alert-ico"><Icon name="alertTri" size={18} /></span>
        <span>{t('sale.credit.missing')}</span>
      </div>}
    {open && <CreditNoteDialog s={s} onClose={() => setOpen(false)} />}
  </Card>;
}

Object.assign(window, { CreditNoteSection, creditNoteLinkOptions, creditNoteCandidateRows, creditNoteReferenceValue, creditNoteSourceIsLocked });
